"use client"

import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Column } from "@tanstack/react-table"
import { GripVertical } from "lucide-react"
import { DropdownMenuCheckboxItem } from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { useShellTranslation } from "@/lib/shell/use-shell-translation"

interface SortableColumnItemProps<TData> {
  column: Column<TData, unknown>
  displayName: string
}

export function SortableColumnItem<TData>({ column, displayName }: SortableColumnItemProps<TData>) {
  const { t } = useShellTranslation()
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: column.id, attributes: { roleDescription: t("shell.table.drag.roleDescription") } })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const isPrimary = !column.getCanHide()

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center justify-between px-2 py-1 hover:bg-accent rounded-sm",
        isPrimary && "opacity-60"
      )}
    >
      <DropdownMenuCheckboxItem
        className="capitalize flex-1 focus:bg-transparent"
        checked={isPrimary ? true : column.getIsVisible()}
        disabled={isPrimary}
        onCheckedChange={isPrimary ? undefined : (value) => column.toggleVisibility(!!value)}
        onSelect={(e) => e.preventDefault()}
      >
        {displayName}
      </DropdownMenuCheckboxItem>
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
    </div>
  )
}
